<template>
  <div class="d-flex flex-column justify-content-start align-items-center" v-if="Array.isArray(rows) && rows.length">
    <LeaderboardPrizes v-if="prizes" :hide-descriptions="hidePrizeDescriptions"
      :first="prizes.first" :second="prizes.second" :third="prizes.third"
      :isRaffle="raffleMode" :scores="rows.slice(0, 3)" />

    <!-- Draw Winner Button -->
    <div v-if="raffleMode && isRecruiterAdmin" class="w-100">
      <b-btn block variant="primary" class="w-100 mt-4" v-b-modal.raffle-spinner>
        Draw Winner
      </b-btn>
    </div>

    <!-- Draw Winner Modal -->
    <b-modal id="raffle-spinner" hide-footer no-body lazy>
        <raffle-spinner
          class="p-3"
          :participants="rows.filter(({ user }) => user).map(({ user }) => ({ id: user.id, name: user.fullName }))"
          :totals="rows.filter(({ user }) => user).reduce((l, { user, points: total }) => ({ ...l, [user.id]: total }), {})"
          :previousWinners="Array.isArray(mainPrizeWinners) ? mainPrizeWinners : []"
          @selected-winner="userID => selectedMainPrizeWinner(userID)"
        />
    </b-modal>

    <!-- Point System Accordion -->
    <div class="accordion my-4 w-100" role="tablist">
      <b-card no-body class="mb-1">
        <b-card-header class="d-flex justify-content-between align-items-center">
          <div @click="displayScoreSystem = !displayScoreSystem" v-b-toggle.point-system-description>
            <strong>How to earn points?</strong>
          </div>
          <b-button @click="displayScoreSystem = !displayScoreSystem" v-b-toggle.point-system-description variant="outline-primary" class="px-2" size="sm">
            <font-awesome-icon v-if="displayScoreSystem" icon="chevron-up" size="sm"/>
            <font-awesome-icon v-else icon="chevron-down" size="sm"/>
          </b-button>
        </b-card-header>
        <!-- Point System Collapse -->
        <b-collapse id="point-system-description" visible accordion="point-system-accordion" role="tabpanel">
          <b-card-body>
            <template v-for="(score, key) in this.scores">
              <b-row class="point-type pb-2 d-flex justify-content-start align-items-center" :key="`${key}-${score.id}`">
                <div class="icon-container h5 my-2 mx-3">
                  <div class="bg-primary-a2 round-icon text-center" v-if="score.icon">
                    <font-awesome-icon :icon="score.icon"/>
                  </div>
                </div>
                <div>
                  <span>
                    <b>{{ score.value }} {{ raffleMode ? 'ticket(s)' : 'point(s)' }}</b> earned for <b>{{ score.label }}</b>
                  </span>
                  <span v-if="score.description">
                    <br>{{ score.description }}
                  </span>
                </div>
              </b-row>
            </template>
          </b-card-body>
        </b-collapse>
      </b-card>
    </div>

    <b-table :fields="fields" :items="rows" primary-key="entryID" responsive striped
      class="border my-3" id="event-table" :current-page="page" :per-page="100">
        <template #head(position)>{{ raffleMode ? '🎟' : '🏆' }}</template>
        <template #cell(position)="{ item: { points }, index }">
          <template v-if="points > 0">
            <template v-if="raffleMode">🎟</template>
            <img src="/static/images/icons/medals/gold-medal.svg" alt="First Place" v-else-if="index === 0"/>
            <img src="/static/images/icons/medals/silver-medal.svg" alt="Second Place" v-else-if="index === 1"/>
            <img src="/static/images/icons/medals/bronze-medal.svg" alt="Third Place" v-else-if="index === 2"/>
            <template v-else>{{index + 1}}</template>
          </template>
          <template v-else>-</template>
        </template>
    </b-table>

    <div class="d-flex justify-content-center align-items-center">
      <b-pagination class="m-0" v-model="page" :per-page="100" :total-rows="rows.length"/>
    </div>
  </div>
</template>

<script>
import LeaderboardPrizes from '@/components/leaderboards/Prizes';
import RaffleSpinner from '@src/components/leaderboards/RaffleSpinner';
import { mapRoot } from '@src/mixins/root';

export default {
  props: {
    raffleMode: {
      type: Boolean,
      default: false,
    },
    prizes: {
      type: Object,
      default: null,
    },
    rows: {
      type: Array,
      required: true,
    },
    scores: {
      type: Array,
      required: true,
    },
    hidePrizeDescriptions: {
      type: Boolean,
      default: null,
    },
    campaignID: {
      type: String,
      default: null
    }
  },
  components: {
    LeaderboardPrizes,
    RaffleSpinner
  },
  data: () => ({
    page: 1,
    displayScoreSystem: true,
    mainPrizeWinners: null
  }),
  computed: {
    ...mapRoot([ 'userID', 'isRecruiterAdmin' ]),
    fields() {
      return [
        { key: 'position' },
        {
          key: 'name',
          formatter: (v, k, { user }) => user.fullName
        },
        ...((Array.isArray(this.scores) ? this.scores : []).reduce((scoreFields, score) => {
          if (score && score.id && Array.isArray(score.eventNames) && !score.disabled) {
            const { id, label, eventNames, value } = score;
            scoreFields.push({
              key: `scores.${id}`,
              label: label || id,
              formatter: (v, k, item) => eventNames.reduce((t, e) => {
                const r = ((item || {}).stats || {})[`${e}::POINTS`] || 0;
                t += r ? Math.round(r / value) : 0;
                return t;
              }, 0),
            });
          }
          return scoreFields;
        }, [])),
        { key: 'points' },
      ];
    }
  },
  watch: {
    page() {
      this.$scrollTo('#event-leaderboard-table');
    },
  },
  methods: {
    async selectedMainPrizeWinner(userID) {
      try {
        this.$assert(!Array.isArray(this.mainPrizeWinners) || !this.mainPrizeWinners.includes(userID),
          'Did not expect mainPrizeWinners to have this userID already', { list: this.mainPrizeWinners, userID });

        this.mainPrizeWinners = (Array.isArray(this.mainPrizeWinners) ? this.mainPrizeWinners : []).concat(userID);
        this.$log.debug(JSON.stringify(this.mainPrizeWinners));

        const { campaignID } = this;
        await this.$root.captureLog({
          eventName: 'CAMPAIGN_PRIZE_WINNER',
          campaign: { id: campaignID },
          user: { id: userID },
        });
      } catch (err) {
        this.$log.error(err);
      }
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/campaign/LeaderboardV2.vue